import { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { foodApiService } from "../services/foodApiService";
import { useFoodManagement } from "./useFoodManagement";
import GluceraNavigationRoutes from "../utils/navigationRoutes";

/**
 * Shared meal-image analysis flow (predictFood → context + scanned-food-detail).
 * Uses async + local state (no useMutation) so it works even if React Query fails to load.
 */
export function useMealPhotoAnalyze() {
  const navigate = useNavigate();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const analyzeLockRef = useRef(false);

  const {
    setScannedData,
    setFoodItemsFromBackend,
    setUploadedImage,
    setDocumentId,
    setGhiReasons,
    setGhiScore,
    setGhiLabel,
    setBbsScore,
    setBbsLabel,
    setOtherDetails,
    setGlucoseSpikeData,
  } = useFoodManagement();

  useEffect(() => {
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
    };
  }, []);

  const clearProgressTimer = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  const startProgressSimulation = useCallback(() => {
    clearProgressTimer();
    setUploadProgress(0);
    progressTimerRef.current = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 99) return 99;
        if (prev < 90) return Math.min(prev + 10, 90);
        if (prev < 95) return Math.min(prev + 2, 95);
        return Math.min(prev + 1, 99);
      });
    }, 250);
  }, [clearProgressTimer]);

  const analyzeMealPhoto = useCallback(
    async (file: File) => {
      if (analyzeLockRef.current) return;
      analyzeLockRef.current = true;
      setIsAnalyzing(true);
      startProgressSimulation();

      try {
        const response = await foodApiService.predictFood(file);

        clearProgressTimer();
        setUploadProgress(100);
        setTimeout(() => setUploadProgress(0), 500);

        let rawApiResponse: any = null;
        try {
          rawApiResponse = (response.data as any)?._rawResponse;
          if (rawApiResponse) {
            localStorage.setItem(
              "glucera_analyze_response",
              JSON.stringify(rawApiResponse)
            );
            if (rawApiResponse.date) {
              localStorage.setItem("glucera_meal_date", rawApiResponse.date);
            }
          }
        } catch (error) {
          console.error("Error storing analyze response:", error);
        }

        const responseData: any =
          response.data?.data ?? (response.data as any)?.result ?? response.data;
        const nutrients =
          (responseData as any)?.nutrients ??
          (responseData as any)?.nutrition ??
          (responseData as any)?.nutritional_info;
        const foodItems =
          responseData?.food_items ??
          (responseData as any)?.foods ??
          (responseData as any)?.items ??
          (responseData as any)?.predictions;
        const imageUrl =
          responseData?.image_url ??
          (responseData as any)?.image ??
          (responseData as any)?.url;
        const docId =
          responseData?.document_id ??
          (responseData as any)?.id ??
          (responseData as any)?.doc_id;
        const ghiReasons =
          responseData?.ghi_reasons ??
          rawApiResponse?.ghi_reasons ??
          rawApiResponse?.reasons ??
          [];
        const ghiScore =
          responseData?.ghi_score ?? rawApiResponse?.ghi_score ?? null;
        const ghiLabel =
          responseData?.ghi_label ?? rawApiResponse?.ghi_label ?? null;
        const bbsScore =
          responseData?.bbs_score ?? rawApiResponse?.bbs_score ?? rawApiResponse?.glucose_spike?.bbs_score ?? null;
        const bbsLabel =
          responseData?.bbs_label ?? rawApiResponse?.bbs_label ?? rawApiResponse?.glucose_spike?.bbs_label ?? null;
        const otherDetails =
          responseData?.other_details ?? rawApiResponse?.other_details ?? null;
        const glucoseSpike =
          responseData?.glucose_spike ?? rawApiResponse?.glucose_spike ?? null;
        const glucoseCurve =
          responseData?.glucose_curve ?? rawApiResponse?.glucose_curve ?? null;

        if (responseData) {
          localStorage.removeItem("glucera_food_items");
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith("glucera_manual_meal_")) keysToRemove.push(key);
          }
          keysToRemove.forEach((key) => localStorage.removeItem(key));

          if (nutrients) setScannedData(nutrients);
          if (foodItems && foodItems.length > 0)
            setFoodItemsFromBackend(foodItems);
          if (imageUrl) setUploadedImage(imageUrl);
          if (docId) setDocumentId(docId);
          if (ghiReasons && Array.isArray(ghiReasons) && ghiReasons.length > 0)
            setGhiReasons(ghiReasons);
          if (ghiScore !== null && ghiScore !== undefined)
            setGhiScore(ghiScore);
          if (ghiLabel) setGhiLabel(ghiLabel);
          if (bbsScore !== null && bbsScore !== undefined)
            setBbsScore(bbsScore);
          if (bbsLabel) setBbsLabel(bbsLabel);
          if (otherDetails) setOtherDetails(otherDetails);
          if (glucoseSpike) {
            setGlucoseSpikeData(
              glucoseCurve
                ? { ...glucoseSpike, glucose_curve: glucoseCurve }
                : glucoseSpike
            );
          }

          setTimeout(() => {
            try {
              navigate("/glucera" + GluceraNavigationRoutes.SCANNED_FOOD_DETAIL);
            } catch {
              window.location.href = "/glucera/scanned-food-detail";
            }
          }, 500);
        } else {
          message.error(
            "No food items detected in the image. Please try again with a clearer image."
          );
        }
      } catch {
        clearProgressTimer();
        setUploadProgress(0);
        message.error("Failed to analyze the image. Please try again.");
      } finally {
        analyzeLockRef.current = false;
        setIsAnalyzing(false);
      }
    },
    [
      clearProgressTimer,
      navigate,
      setBbsLabel,
      setBbsScore,
      setDocumentId,
      setFoodItemsFromBackend,
      setGhiLabel,
      setGhiReasons,
      setGhiScore,
      setGlucoseSpikeData,
      setOtherDetails,
      setScannedData,
      setUploadedImage,
      startProgressSimulation,
    ]
  );

  return {
    analyzeMealPhoto,
    isAnalyzing,
    uploadProgress,
  };
}
